import { Dialog } from "primereact/dialog";
import styles from "./BulkEditModal.module.css";
import { Button } from "primereact/button";
import { useState, useEffect, useRef } from "react";
import { sleepMs } from "../../../util";
import { format } from "date-fns";
import { useUpdateAddresses, useUploadAdditionalFiles } from "../data";
import { SelectButton } from "primereact/selectbutton";
import { useToast } from "../../../context-providers/Toast";
import { RawAddressWithIndices } from "./types";
import { applyOverridesToAddress } from "../Conversion";
import { GMAPS_SRC } from "../../../config";

export type StreetViewModalState = { data: RawAddressWithIndices } | undefined;

// Define Google Maps types for TypeScript
declare global {
  interface Window {
    // eslint-disable-next-line
    google: any;
    initStreetView?: () => void;
  }
}

type StreetViewProps = {
  initialState: StreetViewModalState;
  onHide: () => void;
  isLightMode: boolean;
  reportId: string;
};

const imageFromSatellite = async (mapElement: HTMLDivElement) => {
  // Find the element that contains all the visible map tiles
  const tilesContainer = mapElement.querySelector(
    ".gm-style div[style*='z-index: 0'] > div[style*='position: absolute'][style*='transform']"
  );

  if (!tilesContainer) {
    throw new Error("Could not find the tiles container");
  }

  // Create a canvas directly, matching the map dimensions
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to load ctx");
  }
  const width = mapElement.offsetWidth;
  const height = mapElement.offsetHeight;

  canvas.width = width;
  canvas.height = height;

  // Google Maps default background color
  ctx.fillStyle = "#e5e3df";
  ctx.fillRect(0, 0, width, height);

  // Get all the visible map tiles
  const tiles = Array.from(
    tilesContainer.querySelectorAll("img[src*='googleapis.com']")
  ) as HTMLImageElement[];

  const fetchImageAsDataURL = async (url: string): Promise<string> => {
    const response = await fetch(url, {
      mode: "cors",
      cache: "no-cache",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Create an array to store all the tile processing promises
  const tilePromises = tiles.map(async (tile) => {
    // Get the position of the tile
    const tileRect = tile.getBoundingClientRect();
    const mapRect = mapElement.getBoundingClientRect();
    const x = tileRect.left - mapRect.left;
    const y = tileRect.top - mapRect.top;

    // Fetch the image as a data URL
    const dataUrl = await fetchImageAsDataURL(tile.src);

    // Return the tile data for later processing
    return {
      dataUrl,
      x,
      y,
      width: tile.width,
      height: tile.height,
    };
  });

  // Wait for all tiles to be processed
  const tileResults = await Promise.all(tilePromises);

  // Now draw all the tiles to the canvas
  for (const tileData of tileResults) {
    const { dataUrl, x, y, width, height } = tileData;
    const img = new Image();
    await new Promise<void>((resolve) => {
      img.onload = () => {
        ctx.drawImage(img, x, y, width, height);
        resolve();
      };
      img.onerror = () => {
        resolve();
      };
      img.src = dataUrl;
    });
  }

  return canvas.toDataURL("image/png");
};

const imageFromStreetView = async (streamWrapper: {
  stream: MediaStream | null;
}) => {
  const canvasElement = document.querySelector(
    ".widget-scene-canvas"
  ) as HTMLCanvasElement;
  if (!canvasElement) {
    throw new Error("Street View canvas not found");
  }

  // Use captureStream to get a MediaStream from the canvas
  streamWrapper.stream = canvasElement.captureStream(30); // 30 FPS

  // Create a video element to capture a frame
  const video = document.createElement("video");
  video.srcObject = streamWrapper.stream;

  // Wait for the video to have enough data before playing
  await new Promise<void>((resolve) => {
    video.onloadeddata = () => resolve();
    video.play();
  });

  // Wait for a few frames to ensure quality
  await sleepMs(100);

  // Create a new canvas to draw the video frame
  const captureCanvas = document.createElement("canvas");
  captureCanvas.width = canvasElement.width;
  captureCanvas.height = canvasElement.height;
  const ctx = captureCanvas.getContext("2d");

  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  // Draw the current video frame to the canvas
  ctx.drawImage(video, 0, 0, captureCanvas.width, captureCanvas.height);
  return captureCanvas.toDataURL("image/png");
};

export const StreetViewModal: React.FC<StreetViewProps> = ({
  initialState,
  onHide,
  isLightMode,
  reportId,
}) => {
  const updateAddresses = useUpdateAddresses(reportId);
  const [state, setState] = useState<StreetViewModalState | undefined>();
  const [isRecenteringCoords, setIsRecenteringCoords] = useState(false);
  const isRecenteringCoordsRef = useRef(isRecenteringCoords);
  useEffect(() => {
    isRecenteringCoordsRef.current = isRecenteringCoords;
  }, [isRecenteringCoords]);

  useEffect(() => {
    setState(initialState);
  }, [initialState]);

  const modeClass = isLightMode ? styles.light : styles.dark;
  void modeClass;

  const toast = useToast();
  const [panoramaLoaded, setPanoramaLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mapType, setMapType] = useState<"Satellite" | "Street View">(
    "Street View"
  );
  const [mapDivElement, setMapDivElement] = useState<HTMLDivElement | null>(
    null
  );

  // Store view states
  const [satelliteCenter, setSatelliteCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [satelliteZoom, setSatelliteZoom] = useState<number>(18);
  const [streetViewPov, setStreetViewPov] = useState<{
    heading: number;
    pitch: number;
  }>({ heading: 0, pitch: 10 });
  const [streetViewPosition, setStreetViewPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const uploadFiles = useUploadAdditionalFiles(reportId, () => {}, true);
  const initialLat = Number(state?.data.lat);
  const initialLng = Number(state?.data.long);

  const initStreetView = () => {
    if (!mapDivElement || !state) return;

    if (mapType === "Satellite") {
      // Initialize or restore satellite view
      const mapOptions = {
        center: satelliteCenter || { lat: initialLat, lng: initialLng },
        zoom: satelliteZoom,
        mapTypeId: "satellite",
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      };

      const map = new window.google.maps.Map(mapDivElement, mapOptions);

      map.addListener("click", async (event: google.maps.MapMouseEvent) => {
        if (isRecenteringCoordsRef.current && event.latLng) {
          const clickedLat = event.latLng.lat();
          const clickedLng = event.latLng.lng();
          const addressToUpdate = state.data.__extras.fullAddress;

          addressToUpdate.private_overrides["lat"] = clickedLat.toString();
          addressToUpdate.private_overrides["long"] = clickedLng.toString();

          const result = await updateAddresses.mutateAsync({
            addresses: [addressToUpdate],
          });
          if (result.addresses.length === 1) {
            const address = result.addresses[0];
            const rawAddress = applyOverridesToAddress(address);
            const newState: RawAddressWithIndices = {
              ...rawAddress,
              __extras: { fullAddress: address },
            };
            setState({ data: newState });
          }

          setIsRecenteringCoords(false);
        }
      });

      new window.google.maps.Marker({
        position: { lat: initialLat, lng: initialLng },
        map: map,
        title: state.data.street_address,
        animation: window.google.maps.Animation.DROP,
      });

      // Add a second marker for the Street View position if it exists
      if (streetViewPosition) {
        const streetViewMarker = new window.google.maps.Marker({
          position: streetViewPosition,
          map: map,
          title: "Street View Position",
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: "#4285F4", // Google blue
            fillOpacity: 1,
            strokeWeight: 2,
            strokeColor: "#FFFFFF",
            scale: 8,
          },
        });

        const infoWindow = new window.google.maps.InfoWindow({
          content: `<div style="color: #333; padding: 5px;">Street View Camera Position</div>`,
        });

        streetViewMarker.addListener("mouseover", () => {
          infoWindow.open(map, streetViewMarker);
        });

        streetViewMarker.addListener("mouseout", () => {
          infoWindow.close();
        });
      }

      // Listen for map changes to save state
      map.addListener("center_changed", () => {
        const center = map.getCenter();
        if (center) {
          setSatelliteCenter({
            lat: center.lat(),
            lng: center.lng(),
          });
        }
      });

      map.addListener("zoom_changed", () => {
        const zoom = map.getZoom();
        if (zoom) {
          setSatelliteZoom(zoom);
        }
      });
    } else {
      // Initialize or restore street view
      const position = streetViewPosition || {
        lat: initialLat,
        lng: initialLng,
      };
      const panorama = new window.google.maps.StreetViewPanorama(
        mapDivElement,
        {
          position: position,
          pov: streetViewPov,
          zoom: 1,
          fullscreenControl: false,
          addressControl: false,
          imageDateControl: true,
        }
      );

      // Listen for panorama changes to save state
      panorama.addListener("position_changed", () => {
        const position = panorama.getPosition();
        if (position) {
          const newPos = {
            lat: position.lat(),
            lng: position.lng(),
          };
          setStreetViewPosition(newPos);
        }
      });

      panorama.addListener("pov_changed", () => {
        const pov = panorama.getPov();
        setStreetViewPov({
          heading: pov.heading,
          pitch: pov.pitch,
        });
      });
    }

    setPanoramaLoaded(true);
  };

  useEffect(() => {
    if (!state || !mapDivElement) return;

    if (window.google && window.google.maps) {
      initStreetView();
      return;
    }

    window.initStreetView = initStreetView;

    const script = document.createElement("script");
    script.src = GMAPS_SRC;
    script.async = true;
    script.defer = true;

    document.head.appendChild(script);

    return () => {
      window.initStreetView = undefined;
      setPanoramaLoaded(false);
    };
  }, [state, mapDivElement, mapType]);

  const showSuccessSnackbar = () => {
    toast.show({
      severity: "success",
      summary: "Screenshot Saved",
      detail: 'You can find this screenshot on the "Attachments" tab.',
      life: 5000,
    });
  };

  const showFailureSnackbar = () => {
    toast.show({
      severity: "warn",
      summary: "Failed to Save Screenshot",
      detail: "Something went wrong while saving this screenshot.",
      life: 5000,
    });
  };

  const captureScreenshot = async () => {
    if (!state) return;
    const streamWrapper: { stream: MediaStream | null } = { stream: null };
    setIsLoading(true);

    try {
      let imageUrl: string = "";
      if (mapType === "Satellite") {
        if (!mapDivElement) {
          throw new Error("No map div element");
        }
        imageUrl = await imageFromSatellite(mapDivElement);
      } else {
        imageUrl = await imageFromStreetView(streamWrapper);
      }

      const fetchResponse = await fetch(imageUrl);
      const blob = await fetchResponse.blob();
      const formattedDateTime = format(new Date(), "MM-dd-yyyy_HH:mm:ss");

      // Limit to alphanumeric and a few other characters
      const leadName = mapType === "Street View" ? "street-view" : "satellite";
      const filename =
        `${leadName}_${state.data.street_address}_${formattedDateTime}.png`
          .replaceAll(" ", "-")
          .replace(/[^a-zA-Z0-9_\-.:]/g, "");
      const file = new File([blob], filename, { type: "image/png" });

      // Our catch should get the errors
      await uploadFiles.mutateAsync([file], {
        onSuccess: () => showSuccessSnackbar(),
      });
    } catch (error) {
      console.error("Error capturing screenshot:", error);
      showFailureSnackbar();
    } finally {
      if (streamWrapper.stream) {
        streamWrapper.stream.getTracks().forEach((track) => track.stop());
      }

      setIsLoading(false);
    }
  };

  return (
    <Dialog
      visible={!!state}
      onHide={onHide}
      header={
        <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
          <span>Google Maps</span>
          <SelectButton
            disabled={isLoading}
            value={mapType}
            onChange={(e) => setMapType(e.value)}
            options={["Street View", "Satellite"]}
          />
          <Button
            icon="pi pi-camera"
            loading={isLoading}
            label="Capture Screenshot"
            onClick={captureScreenshot}
            disabled={!panoramaLoaded}
          />
          {mapType === "Satellite" && (
            <>
              <Button
                disabled={updateAddresses.isPending}
                label={isRecenteringCoords ? "Cancel" : "Re-center Coordinates"}
                onClick={() => setIsRecenteringCoords(!isRecenteringCoords)}
                severity="secondary"
                loading={updateAddresses.isPending}
              />
              {isRecenteringCoords && (
                <span
                  style={{
                    fontSize: "12px",
                    marginLeft: "-12px",
                    maxWidth: "170px",
                  }}
                >
                  Click on the map to adjust the coordinates of the location
                </span>
              )}
            </>
          )}
        </div>
      }
      draggable={false}
      style={{
        width: "calc(100vw - 72px)",
        height: "calc(100vh - 72px)",
      }}
    >
      {state?.data.street_address && state?.data.location_description ? (
        <>
          "{state.data.street_address}" described as "
          {state?.data.location_description}"
        </>
      ) : null}
      <div ref={setMapDivElement} style={{ width: "100%", height: "100%" }} />
    </Dialog>
  );
};
